import React from 'react';
import {Drawer} from 'shared/ui/Drawer';
import type {OffersExtraFilters} from '../../model/offersFilters';
import styles from './OffersFilterDrawer.module.css';

export type {OffersExtraFilters};

const MIN_REWARD_RUBLES = 100;
const MAX_REWARD_RUBLES = 500_000;
const REWARD_STEP_RUBLES = 100;

interface OffersFilterDrawerProps {
	open: boolean;
	onClose: () => void;
	value: OffersExtraFilters;
	onApply: (value: OffersExtraFilters) => void;
}

function rublesFromCents(cents?: number): number {
	if (cents == null) return MIN_REWARD_RUBLES;
	return Math.min(MAX_REWARD_RUBLES, Math.max(MIN_REWARD_RUBLES, Math.round(cents / 100)));
}

function formatRubles(amount: number): string {
	return `${amount.toLocaleString('ru-RU')} ₽`;
}

export function OffersFilterDrawer({open, onClose, value, onApply}: OffersFilterDrawerProps): React.ReactElement {
	const [rewardRubles, setRewardRubles] = React.useState(() => rublesFromCents(value.rewardCentsFrom));
	const [includeHidden, setIncludeHidden] = React.useState(Boolean(value.includeHidden));

	React.useEffect(() => {
		if (!open) return;
		setRewardRubles(rublesFromCents(value.rewardCentsFrom));
		setIncludeHidden(Boolean(value.includeHidden));
	}, [open, value.includeHidden, value.rewardCentsFrom]);

	const isMinReward = rewardRubles <= MIN_REWARD_RUBLES;

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={<span className={styles.drawerTitle}>Фильтр</span>}
			showCloseButton
			closeButtonPosition="end"
			bodyClassName={styles.drawerBody}
			footerClassName={styles.drawerFooter}
			footerContent={
				<div className={styles.footer}>
					<button
						type="button"
						className={styles.applyButton}
						onClick={() => {
							onApply({
								rewardCentsFrom: rewardRubles > MIN_REWARD_RUBLES ? rewardRubles * 100 : undefined,
								includeHidden: includeHidden || undefined,
							});
							onClose();
						}}
					>
						Применить
					</button>
				</div>
			}
		>
			<div className={styles.body}>
				<div className={styles.paymentField}>
					<span className={styles.label}>Оплата</span>
					<p className={styles.paymentValue}>
						от{' '}
						<span className={isMinReward ? styles.paymentValueMuted : undefined}>{formatRubles(rewardRubles)}</span>
					</p>
					<input
						type="range"
						className={styles.slider}
						min={MIN_REWARD_RUBLES}
						max={MAX_REWARD_RUBLES}
						step={REWARD_STEP_RUBLES}
						value={rewardRubles}
						aria-label="Оплата от"
						onChange={(event) => setRewardRubles(Number(event.target.value))}
						style={
							{
								'--slider-progress': `${((rewardRubles - MIN_REWARD_RUBLES) / (MAX_REWARD_RUBLES - MIN_REWARD_RUBLES)) * 100}%`,
							} as React.CSSProperties
						}
					/>
				</div>

				<button
					type="button"
					className={styles.toggleRow}
					aria-pressed={includeHidden}
					onClick={() => setIncludeHidden((current) => !current)}
				>
					<span className={styles.toggleLabel}>Показывать скрытые предложения</span>
					<span className={`${styles.switch} ${includeHidden ? styles.switchOn : ''}`} aria-hidden="true">
						<span className={styles.switchThumb} />
					</span>
				</button>
			</div>
		</Drawer>
	);
}
